import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { CampaignsOverviewStats } from "@signalist/shared-types";

/** `Number(...)`-guarded share of `value` in `of`, rounded — SQL counts can arrive as strings. */
export const ratePercent = (value: number, of: number): number => {
	const numerator = Number(value ?? 0) || 0;
	const denominator = Number(of ?? 0) || 0;

	return denominator > 0 ? Math.round((numerator / denominator) * 100) : 0;
};

const Stat: FunctionComponent<{
	label: string;
	value: string;
	detail: string;
	valueClassName?: string;
}> = ({ label, value, detail, valueClassName }) => (
	<div className="flex flex-1 flex-col gap-0.5 border-r border-border-light px-4 py-2.5 last:border-r-0">
		<span className="text-[10px] font-semibold uppercase tracking-wider text-text-meta">
			{label}
		</span>
		<span className="flex items-baseline gap-1.5 text-base font-bold tabular-nums">
			<span className={valueClassName}>{value}</span>
			<span className="text-xs font-medium text-text-meta">{detail}</span>
		</span>
	</div>
);

/**
 * Stat strip of the Campaigns overview (PREC-3880): running campaigns, contacted, replied and
 * positive across the active ownership scope, from the batched `/list/campaigns-overview`
 * aggregates (not from the loaded page, so the numbers are stable while scrolling).
 */
export const CampaignsStatStrip: FunctionComponent<{ stats?: CampaignsOverviewStats }> = ({
	stats,
}) => {
	const { t } = useTranslation();

	if (!stats) {
		return null;
	}

	return (
		<div className="mb-4 flex items-center overflow-hidden rounded-[10px] border border-border-light bg-gray-50">
			<Stat
				detail={t("campaignsOverview.stats.runningDetail", {
					total: stats.totalCampaigns,
					lists: stats.listCount,
				})}
				label={t("campaignsOverview.stats.running")}
				value={`${stats.runningCampaigns}`}
			/>
			<Stat
				detail={t("campaignsOverview.stats.contactedDetail")}
				label={t("campaignsOverview.stats.contacted")}
				value={stats.contacted.toLocaleString()}
			/>
			<Stat
				detail={t("campaignsOverview.stats.repliedDetail", {
					rate: ratePercent(stats.replied, stats.contacted),
				})}
				label={t("campaignsOverview.stats.replied")}
				value={stats.replied.toLocaleString()}
			/>
			<Stat
				detail={t("campaignsOverview.stats.positiveDetail", {
					rate: ratePercent(stats.positive, stats.contacted),
				})}
				label={t("campaignsOverview.stats.positive")}
				value={stats.positive.toLocaleString()}
				valueClassName="text-status-success"
			/>
		</div>
	);
};
